"use client";

import React from 'react';
import { usePathname } from 'next/navigation';

export default function CreateCVLoading({ hideHeader = false }: { hideHeader?: boolean }) {
  const pathname = usePathname();
  const isEn = pathname?.startsWith('/en');

  return (
    <div style={{ backgroundColor: '#f9fafb', minHeight: '100vh', width: '100%' }}>
      {!hideHeader && (
        <header className="container" style={{ 
          padding: '2.5rem 1.5rem 2rem', 
          maxWidth: '960px', 
          margin: '0 auto', 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <h1 style={{ 
            fontSize: '2.15rem', 
            fontWeight: 800, 
            color: '#111827', 
            margin: '0 0 0.75rem 0', 
            letterSpacing: '-0.025em',
            lineHeight: 1.25,
            textAlign: 'center'
          }}>
            {isEn ? 'Free Online Resume & CV Builder' : 'Krijo CV Profesionale Falas Online'}
          </h1>
          <p style={{ 
            fontSize: '1.05rem', 
            color: '#4b5563', 
            margin: '0 auto', 
            lineHeight: 1.6, 
            maxWidth: '800px',
            textAlign: 'center'
          }}>
            {isEn
              ? 'Build and download your professional resume online for free with MyJobs.al. Choose modern templates, customize your profile, and export instantly to PDF.'
              : 'Ndërtoni CV-në tuaj profesionale falas në pak minuta me MyJobs.al. Zgjidhni modele moderne, plotësoni të dhënat tuaja dhe shkarkoni në format PDF menjëherë.'}
          </p>
        </header>
      )}

      <div style={{
        display: 'flex',
        gap: '2rem',
        padding: hideHeader ? '2rem' : '0 2rem 2rem 2rem',
        maxWidth: '1600px',
        margin: '0 auto',
        minHeight: '100vh',
        boxSizing: 'border-box'
      }}>
      
      {/* 1. Leftmost Toolbox (360px width, 414px height) */}
      <div style={{ width: '360px', flexShrink: 0 }}>
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '414px',
          boxSizing: 'border-box'
        }}>
          {/* Header Title with Free Badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <span style={{ background: '#ecfdf5', color: '#10b981', padding: '0.1rem 0.5rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 800 }}>FREE</span>
            <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827' }}>CV Builder</span>
          </div>

          {/* Description Text */}
          <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1rem', lineHeight: 1.4 }}>
            Click any text directly on the CV to edit it inline. Drag and drop sections using the dotted handle to reorder them.
          </p>
          <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: 1.4 }}>
            <strong style={{ color: '#374151' }}>Note:</strong> A dashed red line will appear if your content exceeds a single A4 page.
          </p>

          {/* Action Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <div style={{
              height: '42px',
              borderRadius: '6px',
              background: '#e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              color: '#374151',
              fontWeight: 600,
              fontSize: '0.875rem'
            }}>
              <span>📥</span> Import data from Profile
            </div>
            <div style={{
              height: '42px',
              borderRadius: '6px',
              background: '#fee2e2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              color: '#dc2626',
              fontWeight: 600,
              fontSize: '0.875rem'
            }}>
              <span>🗑️</span> Start Blank
            </div>
          </div>

          {/* Download PDF Button */}
          <div style={{
            height: '46px',
            borderRadius: '6px',
            background: '#facc15',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            color: '#1c1917',
            fontWeight: 700,
            fontSize: '0.95rem'
          }}>
            <span>📄</span> Download PDF
          </div>
        </div>
      </div>

      {/* 2. Middle Templates Selector (160px width, 603px height) */}
      <div style={{ width: '160px', flexShrink: 0 }}>
        <div style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '600px',
          boxSizing: 'border-box'
        }}>
          {/* Templates Header */}
          <div style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#111827' }}>
            <span>📑</span> Templates
          </div>

          {/* Color Palet */}
          <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Color Palet</span>
            <div style={{
              width: '100%',
              height: '24px',
              borderRadius: '6px',
              background: 'linear-gradient(to right, #2b2b2b, #4338ca, #10b981, #f59e0b)'
            }} />
          </div>

          {/* Template Thumbnails Placeholders */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Template 1 */}
            <div style={{
              height: '200px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              background: 'linear-gradient(90deg, #ffffff 25%, #f8fafc 50%, #ffffff 75%)',
              backgroundSize: '200% 100%',
              animation: 'skeletonShimmer 1.5s infinite',
              padding: '6px'
            }} />

            {/* Template 2 */}
            <div style={{
              height: '200px',
              borderRadius: '8px',
              border: '2px solid #3b82f6',
              background: 'linear-gradient(90deg, #ffffff 25%, #f8fafc 50%, #ffffff 75%)',
              backgroundSize: '200% 100%',
              animation: 'skeletonShimmer 1.5s infinite',
              padding: '6px'
            }} />
          </div>
        </div>
      </div>

      {/* 3. Rightmost CV Canvas (794px × 1122px A4 format) */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
        
        {/* Top Floating Control Bar */}
        <div style={{
          width: '794px',
          maxWidth: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ width: '140px', height: '24px', borderRadius: '4px', background: '#e5e7eb' }} />
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div style={{ width: '80px', height: '32px', borderRadius: '6px', background: '#dcfce7' }} />
            <div style={{ width: '130px', height: '32px', borderRadius: '6px', background: '#3b82f6' }} />
          </div>
        </div>

        {/* The 794x1122 A4 Document Skeleton */}
        <div style={{
          width: '794px',
          minHeight: '1122px',
          background: 'white',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          display: 'flex',
          position: 'relative',
          overflow: 'hidden'
        }}>
          
          {/* Left Column in CV (Sidebar) */}
          <div style={{
            width: '260px',
            background: 'linear-gradient(180deg, #475569 0%, #334155 100%)',
            padding: '2rem 1.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1.5rem',
            flexShrink: 0
          }}>
            {/* Avatar Circle */}
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              animation: 'skeletonShimmer 1.5s infinite'
            }} />

            {/* Name / Title */}
            <div style={{ width: '80%', height: '20px', borderRadius: '4px', background: 'rgba(255, 255, 255, 0.3)' }} />
            <div style={{ width: '50%', height: '14px', borderRadius: '4px', background: 'rgba(255, 255, 255, 0.2)' }} />

            {/* Contact Details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%', marginTop: '2rem' }}>
              <div style={{ width: '100%', height: '12px', borderRadius: '3px', background: 'rgba(255, 255, 255, 0.2)' }} />
              <div style={{ width: '90%', height: '12px', borderRadius: '3px', background: 'rgba(255, 255, 255, 0.2)' }} />
              <div style={{ width: '80%', height: '12px', borderRadius: '3px', background: 'rgba(255, 255, 255, 0.2)' }} />
            </div>
          </div>

          {/* Right Column in CV (Main Body) */}
          <div style={{
            flex: 1,
            padding: '2.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem'
          }}>
            {/* Section 1: About Me */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ width: '140px', height: '20px', borderRadius: '4px', background: '#cbd5e1' }} />
              <div style={{ width: '100%', height: '12px', borderRadius: '3px', background: '#f1f5f9' }} />
              <div style={{ width: '95%', height: '12px', borderRadius: '3px', background: '#f1f5f9' }} />
              <div style={{ width: '85%', height: '12px', borderRadius: '3px', background: '#f1f5f9' }} />
            </div>

            {/* Section 2: Experience */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ width: '160px', height: '20px', borderRadius: '4px', background: '#cbd5e1' }} />
              {[1, 2, 3].map(i => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ width: '45%', height: '14px', borderRadius: '3px', background: '#94a3b8' }} />
                  <div style={{ width: '30%', height: '12px', borderRadius: '3px', background: '#cbd5e1' }} />
                  <div style={{ width: '100%', height: '10px', borderRadius: '3px', background: '#f1f5f9' }} />
                </div>
              ))}
            </div>

            {/* Section 3: Skills */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ width: '100px', height: '20px', borderRadius: '4px', background: '#cbd5e1' }} />
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1, height: '12px', borderRadius: '3px', background: '#e2e8f0' }} />
                <div style={{ flex: 1, height: '12px', borderRadius: '3px', background: '#e2e8f0' }} />
              </div>
            </div>
          </div>

        </div>

      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes skeletonShimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `
      }} />
      </div>
    </div>
  );
}
